import { api } from "../api";
import type { PracticeOrg } from "../types";
import { esc } from "../utils/html";

function showToast(message: string): void {
  const existing = document.querySelector(".toast");
  if (existing) existing.remove();
  const t = document.createElement("div");
  t.className = "toast";
  t.textContent = message;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add("show"), 10);
  setTimeout(() => { t.classList.remove("show"); setTimeout(() => t.remove(), 300); }, 2500);
}

export async function render(el: HTMLElement) {
  el.innerHTML = `
    <div class="page-container">
      <div class="page-header"><h1>Einstellungen</h1></div>
      <div id="ps-content"><div class="spinner"></div></div>
    </div>`;
  const content = el.querySelector("#ps-content") as HTMLElement;

  let org: PracticeOrg;
  try {
    org = await api.practiceOrg.me();
  } catch (err: any) {
    content.innerHTML = `<div class="alert alert-error">${esc(err.message || "Profil konnte nicht geladen werden.")}</div>`;
    return;
  }

  content.innerHTML = `
    <div class="card" style="padding:1.5rem">
      <h2 style="font-size:1rem;font-weight:600;margin:0 0 1rem">Stammdaten</h2>
      <div class="form-grid">
        <div class="form-group col-span-2"><label>Name der Einrichtung *</label><input id="f-name" value="${esc(org.name)}" /></div>
        <div class="form-group col-span-2">
          <label>Art *</label>
          <select id="f-category">
            <option value="PRAXIS" ${org.category === "PRAXIS" ? "selected" : ""}>Praxis</option>
            <option value="KRANKENHAUS" ${org.category === "KRANKENHAUS" ? "selected" : ""}>Krankenhaus</option>
          </select>
        </div>
        <div class="form-group"><label>Straße</label><input id="f-street" value="${esc(org.street)}" /></div>
        <div class="form-group"><label>Hausnummer</label><input id="f-house" value="${esc(org.house_no)}" /></div>
        <div class="form-group"><label>PLZ *</label><input id="f-plz" value="${esc(org.plz)}" maxlength="5" /></div>
        <div class="form-group"><label>Ort *</label><input id="f-ort" value="${esc(org.ort)}" /></div>
        <div class="form-group"><label>E-Mail *</label><input id="f-email" type="email" value="${esc(org.email)}" /></div>
        <div class="form-group"><label>Telefon</label><input id="f-phone" value="${esc(org.phone)}" /></div>
        <div class="form-group col-span-2"><label>Ansprechpartner:in</label><input id="f-contact" value="${esc(org.contact_person)}" /></div>
      </div>
      <div id="f-error" class="register-error hidden" style="margin-top:1rem"></div>
      <div class="form-actions" style="justify-content:flex-end;margin-top:1.25rem">
        <button class="btn btn-primary" id="f-save">Speichern</button>
      </div>
    </div>

    <div class="card" style="padding:1.5rem;margin-top:1.5rem;border-color:#fecaca">
      <h2 style="font-size:1rem;font-weight:600;margin:0 0 .25rem;color:#dc2626">Einrichtung löschen</h2>
      <div style="font-size:.8125rem;color:var(--gray-500);margin-bottom:1rem">Einrichtung und alle zugehörigen Daten unwiderruflich löschen</div>
      <div id="ps-delete-content"></div>
    </div>
  `;

  content.querySelector("#f-save")!.addEventListener("click", async () => {
    const payload: Partial<PracticeOrg> = {
      name: (content.querySelector("#f-name") as HTMLInputElement).value.trim(),
      category: (content.querySelector("#f-category") as HTMLSelectElement).value as "PRAXIS" | "KRANKENHAUS",
      street: (content.querySelector("#f-street") as HTMLInputElement).value.trim(),
      house_no: (content.querySelector("#f-house") as HTMLInputElement).value.trim(),
      plz: (content.querySelector("#f-plz") as HTMLInputElement).value.trim(),
      ort: (content.querySelector("#f-ort") as HTMLInputElement).value.trim(),
      email: (content.querySelector("#f-email") as HTMLInputElement).value.trim(),
      phone: (content.querySelector("#f-phone") as HTMLInputElement).value.trim(),
      contact_person: (content.querySelector("#f-contact") as HTMLInputElement).value.trim(),
    };
    const errEl = content.querySelector("#f-error")!;
    if (!payload.name || !payload.plz || !payload.ort || !payload.email) {
      errEl.textContent = "Bitte fülle alle Pflichtfelder aus.";
      errEl.classList.remove("hidden");
      return;
    }
    errEl.classList.add("hidden");
    const btn = content.querySelector("#f-save") as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Speichert…";
    try {
      org = await api.practiceOrg.update(payload);
      showToast("Gespeichert");
    } catch (err: any) {
      errEl.textContent = err.message || "Speichern fehlgeschlagen.";
      errEl.classList.remove("hidden");
    } finally {
      btn.disabled = false; btn.textContent = "Speichern";
    }
  });

  // ── Einrichtung löschen (dreistufige Bestätigung, analog zur Schul-Selbstlöschung) ──
  const dc = content.querySelector("#ps-delete-content") as HTMLElement;
  let savedPassword = "";

  function deletePhase1() {
    dc.innerHTML = `
      <div style="background:#fff7f7;border:1px solid #fecaca;border-radius:.5rem;padding:1rem;margin-bottom:1rem">
        <p style="font-weight:600;color:#dc2626;margin:0 0 .5rem 0">Achtung: Diese Aktion ist unwiderruflich!</p>
        <p style="color:var(--gray-600);margin:0;font-size:.875rem">Alle Daten deiner Einrichtung werden dauerhaft gelöscht: Profil, Benutzerkonten und hochgeladene Dokumente. Mit Schulen verbundene Praktikumsstellen-Einträge bleiben bei der jeweiligen Schule als deren eigene Daten erhalten. Diese Aktion kann nicht rückgängig gemacht werden.</p>
      </div>
      <button class="btn" id="ps-del-1" style="background:#dc2626;color:#fff;border-color:#dc2626">Ja, ich möchte die Einrichtung löschen</button>`;
    dc.querySelector("#ps-del-1")!.addEventListener("click", deletePhase2);
  }

  function deletePhase2() {
    dc.innerHTML = `
      <div style="background:#fff7f7;border:1px solid #fecaca;border-radius:.5rem;padding:1rem;margin-bottom:1rem">
        <p style="font-weight:600;color:#dc2626;margin:0 0 .5rem 0">Bitte bestätige deine Identität</p>
        <p style="color:var(--gray-600);margin:0;font-size:.875rem">Gib dein aktuelles Passwort ein, um fortzufahren.</p>
      </div>
      <div class="form-group" style="max-width:24rem">
        <label>Passwort</label>
        <input id="ps-del-pw" type="password" placeholder="Dein aktuelles Passwort" autocomplete="current-password" />
        <div id="ps-del-pw-err" style="color:#dc2626;font-size:.8125rem;margin-top:.25rem;display:none"></div>
      </div>
      <div style="display:flex;gap:.5rem;margin-top:.5rem">
        <button class="btn btn-secondary" id="ps-del-cancel2">Abbrechen</button>
        <button class="btn" id="ps-del-2" style="background:#dc2626;color:#fff;border-color:#dc2626">Weiter</button>
      </div>`;
    dc.querySelector("#ps-del-cancel2")!.addEventListener("click", deletePhase1);
    dc.querySelector("#ps-del-2")!.addEventListener("click", () => {
      const pw = (dc.querySelector("#ps-del-pw") as HTMLInputElement).value;
      if (!pw) {
        const e = dc.querySelector("#ps-del-pw-err") as HTMLElement;
        e.textContent = "Bitte gib dein Passwort ein."; e.style.display = "block";
        return;
      }
      savedPassword = pw;
      deletePhase3();
    });
  }

  function deletePhase3(errorMsg?: string) {
    dc.innerHTML = `
      <div style="background:#fff7f7;border:1px solid #fecaca;border-radius:.5rem;padding:1rem;margin-bottom:1rem">
        <p style="font-weight:600;color:#dc2626;margin:0 0 .5rem 0">Bist du wirklich sicher?</p>
        <p style="color:var(--gray-600);margin:0;font-size:.875rem">Du bist dabei, die Einrichtung <strong>${esc(org.name)}</strong> und alle zugehörigen Daten endgültig zu löschen. Dies kann nicht rückgängig gemacht werden.</p>
      </div>
      ${errorMsg ? `<div style="color:#dc2626;font-size:.875rem;margin-bottom:.75rem">${esc(errorMsg)}</div>` : ""}
      <div style="display:flex;gap:.5rem">
        <button class="btn btn-secondary" id="ps-del-cancel3">Abbrechen</button>
        <button class="btn" id="ps-del-confirm" style="background:#dc2626;color:#fff;border-color:#dc2626">Einrichtung endgültig löschen</button>
      </div>`;
    dc.querySelector("#ps-del-cancel3")!.addEventListener("click", deletePhase2);
    dc.querySelector("#ps-del-confirm")!.addEventListener("click", async () => {
      const dbtn = dc.querySelector("#ps-del-confirm") as HTMLButtonElement;
      dbtn.disabled = true; dbtn.textContent = "Wird gelöscht…";
      try {
        await api.practiceOrg.delete(savedPassword);
        localStorage.clear();
        window.location.href = "/";
      } catch (err: any) {
        savedPassword = "";
        if (err.status === 401 || (err.message && err.message.toLowerCase().includes("passwort"))) {
          deletePhase2();
        } else {
          deletePhase3(err.message || "Löschen fehlgeschlagen.");
        }
      }
    });
  }

  deletePhase1();
}
